import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from "recharts";
import {
  TrendingUp,
  AlertTriangle,
  Info
} from "lucide-react";

export const CashFlowForecast = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [forecast, setForecast] = useState(null);
  const [viewDays, setViewDays] = useState("30");
  const [error, setError] = useState(null);

  const fetchForecast = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/cash-flow/forecast?days=" + viewDays);
      setForecast(response.data);
    } catch (err) {
      console.error("Failed to fetch cash flow forecast:", err);
      setError("Unable to load forecast");
    } finally {
      setLoading(false);
    }
  }, [api, viewDays]);

  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);

  const formatCurrency = (value) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatAxisDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const formatTooltipDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-CA", { 
        weekday: "long", 
        year: "numeric", 
        month: "long", 
        day: "numeric" 
      });
    } catch {
      return dateStr;
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    const data = payload[0].payload;
    const isNegative = data.ending_balance < 0;
    const bills = data.bills || [];
    
    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3 min-w-[200px]">
        <p className="font-medium text-sm mb-2">
          {formatTooltipDate(label)}
        </p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Starting:</span>
            <span>{formatCurrency(data.starting_balance)}</span>
          </div>
          {data.income > 0 && (
            <div className="flex justify-between text-emerald-600">
              <span>+ Income:</span>
              <span>{formatCurrency(data.income)}</span>
            </div>
          )}
          {data.expenses > 0 && (
            <div className="flex justify-between text-rose-600">
              <span>- Bills:</span>
              <span>{formatCurrency(data.expenses)}</span>
            </div>
          )}
          <div className={`flex justify-between font-medium pt-1 border-t ${isNegative ? "text-rose-600" : ""}`}>
            <span>Balance:</span>
            <span>{formatCurrency(data.ending_balance)}</span>
          </div>
          {bills.length > 0 && (
            <div className="pt-2 border-t mt-2">
              <p className="text-xs text-muted-foreground mb-1">Bills due:</p>
              {bills.map((bill, i) => (
                <p key={i} className="text-xs">
                  • {bill.merchant}: {formatCurrency(bill.amount)}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="border-border/50" data-testid="cashflow-forecast-loading">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[250px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !forecast) {
    return (
      <Card className="border-border/50">
        <CardContent className="p-6 text-center text-muted-foreground">
          <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>{error || "Unable to load forecast"}</p>
        </CardContent>
      </Card>
    );
  }

  // Filter chart data for readability
  const dailyForecast = forecast.daily_forecast || [];
  const chartData = dailyForecast.filter((item, index) => {
    const hasBills = item.bills && item.bills.length > 0;
    if (viewDays === "30") return index % 2 === 0 || hasBills;
    if (viewDays === "60") return index % 3 === 0 || hasBills;
    return index % 5 === 0 || hasBills;
  });

  // Calculate Y axis bounds
  const allBalances = dailyForecast.map(d => d.ending_balance);
  const minBalance = Math.min(...allBalances);
  const maxBalance = Math.max(...allBalances);
  const yMin = Math.floor(Math.min(minBalance, 0) / 500) * 500 - 500;
  const yMax = Math.ceil(maxBalance / 500) * 500 + 500;

  const hasShortfall = forecast.has_shortfall;
  const firstShortfallDays = forecast.first_shortfall_days;
  const firstShortfallDate = forecast.first_shortfall_date;
  const shortfalls = forecast.shortfalls || [];

  return (
    <Card className="border-border/50" data-testid="cashflow-forecast">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Cash Flow Forecast
            </CardTitle>
            <CardDescription>
              Projected checking account balance
            </CardDescription>
          </div>
          <Tabs value={viewDays} onValueChange={setViewDays} className="w-auto">
            <TabsList className="h-8">
              <TabsTrigger value="30" className="text-xs px-3">30 Days</TabsTrigger>
              <TabsTrigger value="60" className="text-xs px-3">60 Days</TabsTrigger>
              <TabsTrigger value="90" className="text-xs px-3">90 Days</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Current Balance</p>
            <p className="text-lg font-semibold tabular-nums">
              {formatCurrency(forecast.checking_balance)}
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Expected Income</p>
            <p className="text-lg font-semibold tabular-nums text-emerald-600">
              +{formatCurrency(forecast.total_income)}
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Expected Bills</p>
            <p className="text-lg font-semibold tabular-nums text-rose-600">
              -{formatCurrency(forecast.total_expenses)}
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">End Balance</p>
            <p className={`text-lg font-semibold tabular-nums ${forecast.ending_balance < 0 ? "text-rose-600" : ""}`}>
              {formatCurrency(forecast.ending_balance)}
            </p>
          </div>
        </div>

        {/* Chart */}
        <div className="h-[250px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatAxisDate}
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tickFormatter={(value) => "$" + (value / 1000).toFixed(0) + "k"}
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                domain={[yMin, yMax]}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="5 5" strokeWidth={2} />
              <Area
                type="monotone"
                dataKey="ending_balance"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#balanceGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Shortfall Warning */}
        {hasShortfall && (
          <div className="mt-4 p-4 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-rose-900 dark:text-rose-100">
                  Shortfall detected in {firstShortfallDays} days
                </p>
                <p className="text-sm text-rose-700 dark:text-rose-300 mt-1">
                  Your balance is projected to go negative on {formatTooltipDate(firstShortfallDate)}.
                  Consider transferring funds or rescheduling bills.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* No shortfall - positive message */}
        {!hasShortfall && forecast.ending_balance > 0 && (
          <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
              <TrendingUp className="h-4 w-4" />
              <p className="text-sm font-medium">
                Looking good! No shortfalls expected in the next {viewDays} days.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
